/**
 * main.ts - Earth3D prototype entry point.
 *
 * Wires together the renderer, camera, controls, globe (terrain + MSLP +
 * coastlines), atmosphere level shells, particle system, and the UI control
 * panel into a single animated scene.
 *
 * Data source: tries to load real NOAA GFS data (scripts/fetchGfsData.mjs +
 * decode_gfs.py must have been run at least once -- see README.md). If that
 * data isn't available, falls back to the idealized/synthetic circulation
 * model so the app still runs, with a clearly labeled banner either way.
 */

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { createGlobe } from "./scene/globe";
import { createAtmosphereLevelShells } from "./scene/atmosphereLevels";
import { createParticleSystem } from "./scene/particles";
import { createControlPanel } from "./ui/controlPanel";
import { GfsDataStore } from "./data/gfsData";
import { loadElevationGrid } from "./data/elevationGrid";
import { sampleIdealizedWind } from "./physics/idealizedCirculation";
import { sampleIdealizedMslpHpa } from "./physics/idealizedPressure";
import type { WindSamplerFn, MslpSamplerFn } from "./physics/windSampler";
import { EARTH_RADIUS_UNITS, SCENE_BACKGROUND_HEX } from "./constants";

function assertWebGL2Available(): void {
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl2");
  if (!gl) {
    throw new Error(
      "WebGL2 is not available in this browser/GPU configuration. Check hardware acceleration settings."
    );
  }
}

interface DataSource {
  sampleWind: WindSamplerFn;
  sampleMslpHpa: MslpSamplerFn;
  isLive: boolean;
  cycleLabel: string;
  dataStore: GfsDataStore | null;
}

async function loadDataSource(
  initialMinHpa: number,
  initialMaxHpa: number,
  onProgress: (text: string) => void
): Promise<DataSource> {
  const dataStore = new GfsDataStore();
  try {
    onProgress("Loading NOAA GFS manifest...");
    const manifest = await dataStore.loadManifestAndSurface();
    await dataStore.ensureLevelsLoaded(initialMinHpa, initialMaxHpa, (loaded, total) => {
      onProgress(`Loading GFS pressure levels (${loaded}/${total})...`);
    });
    return {
      sampleWind: (latDeg, lonDeg, pressureHpa) => dataStore.sampleWind(latDeg, lonDeg, pressureHpa),
      sampleMslpHpa: (latDeg, lonDeg) => dataStore.sampleMslpHpa(latDeg, lonDeg),
      isLive: true,
      cycleLabel: manifest.cycle,
      dataStore
    };
  } catch (err) {
    console.warn("Live NOAA GFS data unavailable; falling back to the idealized circulation model:", err);
    return {
      sampleWind: (latDeg, lonDeg, pressureHpa) => sampleIdealizedWind(lonDeg, latDeg, pressureHpa),
      sampleMslpHpa: (latDeg, lonDeg) => sampleIdealizedMslpHpa(lonDeg, latDeg),
      isLive: false,
      cycleLabel: "",
      dataStore: null
    };
  }
}

async function main(): Promise<void> {
  assertWebGL2Available();

  const appEl = document.getElementById("app");
  if (!appEl) throw new Error("Missing #app root element.");

  const canvasContainer = document.createElement("div");
  canvasContainer.id = "canvas-container";
  appEl.appendChild(canvasContainer);

  const panelContainer = document.createElement("div");
  panelContainer.id = "panel-container";
  appEl.appendChild(panelContainer);

  const loadingOverlay = document.createElement("div");
  loadingOverlay.id = "loading-overlay";
  loadingOverlay.textContent = "Loading...";
  appEl.appendChild(loadingOverlay);

  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  canvasContainer.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(SCENE_BACKGROUND_HEX);

  const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.01, 1000);
  camera.position.set(0, 3, 18);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.minDistance = EARTH_RADIUS_UNITS * 1.05;
  controls.maxDistance = 60;

  scene.add(new THREE.AmbientLight(0xffffff, 0.65));
  const sun = new THREE.DirectionalLight(0xffffff, 1.0);
  sun.position.set(10, 6, 8);
  scene.add(sun);

  const controlPanel = createControlPanel(panelContainer);

  const dataSource = await loadDataSource(controlPanel.state.minHpa, controlPanel.state.maxHpa, (text) => {
    loadingOverlay.textContent = text;
  });

  if (dataSource.isLive) {
    controlPanel.setBanner(`Live NOAA GFS data, cycle ${dataSource.cycleLabel}`, false);
  } else {
    controlPanel.setBanner(
      "Live NOAA GFS data unavailable -- showing idealized/synthetic circulation instead.",
      true
    );
  }

  // Loaded once and shared by the globe (terrain displacement) and the particle system (so
  // particles never render as if they're inside a mountain -- see particles.ts).
  let elevationMetersAt: (latDeg: number, lonDeg: number) => number = () => 0;
  try {
    const elevationGrid = await loadElevationGrid();
    elevationMetersAt = elevationGrid.elevationMetersAt;
  } catch (err) {
    console.warn("Real elevation data unavailable; showing a flat (sea-level) globe instead:", err);
  }

  const globe = await createGlobe(dataSource.sampleMslpHpa, dataSource.sampleWind, elevationMetersAt);
  scene.add(globe.group);

  const shells = createAtmosphereLevelShells();
  scene.add(shells.group);

  const particles = createParticleSystem(dataSource.sampleWind, dataSource.sampleMslpHpa, elevationMetersAt);
  scene.add(particles.object3D);

  loadingOverlay.remove();

  let lastLoadedRange = { minHpa: controlPanel.state.minHpa, maxHpa: controlPanel.state.maxHpa };

  function applyState(): void {
    const s = controlPanel.state;
    globe.setHeightExaggeration(s.heightExaggeration);
    globe.setSurfaceColorMode(s.surfaceColorMode);
    globe.setCoastlinesVisible(s.showCoastlines);
    shells.setHeightExaggeration(s.heightExaggeration);
    shells.setVisibleRange(s.minHpa, s.maxHpa);
    shells.group.visible = s.showLevelShells;
    particles.object3D.visible = s.showParticles;

    if (
      dataSource.isLive &&
      dataSource.dataStore &&
      (s.minHpa !== lastLoadedRange.minHpa || s.maxHpa !== lastLoadedRange.maxHpa)
    ) {
      lastLoadedRange = { minHpa: s.minHpa, maxHpa: s.maxHpa };
      dataSource.dataStore.ensureLevelsLoaded(s.minHpa, s.maxHpa).catch((err) => {
        console.warn("Failed to load additional GFS levels for the new pressure range:", err);
      });
    }
  }
  controlPanel.onChange(applyState);
  applyState();

  window.addEventListener("resize", () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  let lastFrameTimeMs = performance.now();
  function animate(): void {
    requestAnimationFrame(animate);
    const nowMs = performance.now();
    const dt = Math.min((nowMs - lastFrameTimeMs) / 1000, 0.1);
    lastFrameTimeMs = nowMs;
    const s = controlPanel.state;

    if (s.showParticles) {
      particles.update(dt, {
        heightExaggeration: s.heightExaggeration,
        verticalMotionExaggeration: s.verticalMotionExaggeration,
        minHpa: s.minHpa,
        maxHpa: s.maxHpa,
        speedMultiplier: s.speedMultiplier,
        devicePixelRatio: renderer.getPixelRatio(),
        viewportWidthPx: renderer.domElement.width,
        viewportHeightPx: renderer.domElement.height
      });
    }

    controls.update();
    renderer.render(scene, camera);
  }
  animate();
}

main().catch((err: unknown) => {
  console.error(err);
  const appEl = document.getElementById("app");
  if (appEl) {
    const message = err instanceof Error ? err.message : String(err);
    appEl.innerHTML = `<div class="fatal-error"><h2>Earth3D failed to start</h2><p>${message}</p></div>`;
  }
});
